/** Admin: connectivity test results sent from /diag (China test). */
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useT } from '../i18n.ts';

interface Row {
  code: string;
  label: string | null;
  userAgent: string | null;
  network: string | null;
  createdAt: string;
  results: Array<{ step: string; ok: boolean; ms?: number | null; p50?: number | null; p95?: number | null; detail?: string | null }>;
}

const browser = (ua: string | null) => {
  if (!ua) return '';
  const m = /MicroMessenger|WeChat|HuaweiBrowser|MiuiBrowser|UCBrowser|QQBrowser|Edg|Chrome|Firefox|Safari/.exec(ua)?.[0] ?? '';
  const os = /iPhone|iPad|Android|Windows|Mac OS X|Linux/.exec(ua)?.[0] ?? '';
  return `${m === 'MicroMessenger' ? 'WeChat' : m} · ${os}`;
};

export function DiagReports() {
  const t = useT();
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => void api<Row[]>('GET', '/api/admin/diag').then(setRows).catch(() => setRows([])), []);
  if (!rows) return <p className="muted">…</p>;
  const get = (r: Row, step: string) => r.results.find((x) => x.step === step);
  const ms = (x?: Row['results'][number]) => (!x ? '–' : !x.ok ? '✗' : x.p50 != null ? `${x.p50}/${x.p95}` : x.ms != null ? String(x.ms) : '✓');
  return (
    <div className="stack">
      <p className="muted small">
        {t('diagIntro')} <code>{location.origin}/diag</code>
      </p>
      {rows.length === 0 ? (
        <p className="muted">{t('diagEmpty')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table small">
            <thead>
              <tr>
                <th>{t('diagCode')}</th>
                <th>{t('diagWho')}</th>
                <th>API ms (p50/p95)</th>
                <th>WS ms</th>
                <th>1 MB</th>
                <th>{t('diagFailures')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code}>
                  <td>
                    <strong>{r.code}</strong>
                    <div className="muted">{new Date(r.createdAt).toLocaleString()}</div>
                  </td>
                  <td>
                    {r.label ?? '—'}
                    <div className="muted">{browser(r.userAgent)} {r.network ?? ''}</div>
                  </td>
                  <td>{ms(get(r, 'api'))}</td>
                  <td>{ms(get(r, 'wsEcho'))}</td>
                  <td>{get(r, 'down1m')?.detail ?? ms(get(r, 'down1m'))}</td>
                  <td>{r.results.filter((x) => !x.ok).map((x) => x.step).join(', ') || '0'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
